import { openExternalUrl } from "../../lib/native/nav";

import React, { useEffect, useRef, useState } from "react";
import { Routes, Route } from "react-router-dom";
import api, { formatApiError, STATUS_LABELS } from "../../lib/api";
import { play } from "../../lib/sound";
import { useAuth } from "../../context/AuthContext";
import { DashboardLayout, StatusBadge, StatCard } from "../../components/Shared";
import TerritoryMap from "../../components/TerritoryMap";
import LiveMap from "../../components/LiveMap";
import WalletStatement from "../../components/WalletStatement";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Bike, Package, Euro, Trophy, MapPin, Phone, Navigation, ShoppingBasket, CheckCircle2, Upload, Target, Gem, Award, Medal, XCircle, Lock, Map as MapIcon, Wallet, Banknote, Bot } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import RiderAI from "./RiderAI";

function RiderMarket() {
  const [avail, setAvail] = useState([]);
  const [mine, setMine] = useState([]);
  const [denied, setDenied] = useState(false);
  const load = async () => {
    try {
      const [a, m] = await Promise.all([api.get("/rider/errands/available"), api.get("/rider/errands/mine")]);
      setAvail(a.data); setMine(m.data); setDenied(false);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  };
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, []);

  const accept = async (id) => { try { await api.put(`/rider/errands/${id}/accept`); load(); toast.success("Commissione accettata"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const setStatus = async (id, status) => { try { await api.put(`/rider/errands/${id}/status`, { status }); load(); toast.success("Stato aggiornato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const saveItems = async (e) => {
    try { await api.put(`/rider/errands/${e.id}/items`, { items: e.items }); load(); toast.success("Prezzi salvati"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setItem = (eid, idx, k, v) => setMine(mine.map((e) => e.id === eid ? { ...e, items: e.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) } : e));
  const uploadReceipt = async (eid, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/errands/${eid}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); toast.success("Scontrino caricato"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (denied) return <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-8 text-center text-zinc-400" data-testid="market-not-authorized">Non sei ancora autorizzato come <b>Rider Mercato</b>. Contatta la Centrale Operativa per l'abilitazione.</div>;

  return (
    <div className="space-y-6" data-testid="rider-market">
      <div>
        <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Commissioni disponibili</h3>
        <div className="space-y-3">
          {avail.map((e) => (
            <div key={e.id} data-testid={`avail-errand-${e.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4 flex items-center justify-between">
              <div><div className="font-semibold flex items-center gap-2"><ShoppingBasket className="h-4 w-4 text-[#00E676]" /> #{e.id.slice(-5)} · {e.items.length} prodotti</div><div className="text-sm text-zinc-400">{e.address}</div></div>
              <Button size="sm" onClick={() => accept(e.id)} data-testid={`accept-errand-${e.id}`} className="bg-[#00E676] text-black rounded-md">Accetta</Button>
            </div>
          ))}
          {avail.length === 0 && <div className="text-zinc-500 py-6 text-center">Nessuna commissione disponibile.</div>}
        </div>
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Le mie commissioni</h3>
        <div className="space-y-4">
          {mine.filter((e) => e.status !== "consegnato" && e.status !== "annullato").map((e) => (
            <div key={e.id} data-testid={`mine-errand-${e.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2"><span className="font-semibold">#{e.id.slice(-5)} · {STATUS_LABELS[e.status] || e.status}</span><span className="text-[#00E676] font-semibold">€{Number(e.total || 0).toFixed(2)}</span></div>
              <div className="text-xs text-zinc-400 mb-2">{e.address}{e.notes ? ` · ${e.notes}` : ""}</div>
              <div className="space-y-2 mb-3">
                {e.items.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={!!it.purchased} data-testid={`item-purchased-${e.id}-${idx}`} onChange={(ev) => setItem(e.id, idx, "purchased", ev.target.checked)} className="h-4 w-4 accent-[#00E676]" /> {it.qty}× {it.name}</label>
                    <Input type="number" step="0.01" placeholder="€ prezzo" value={it.actual_price ?? ""} data-testid={`item-price-${e.id}-${idx}`} onChange={(ev) => setItem(e.id, idx, "actual_price", parseFloat(ev.target.value) || 0)} className="bg-[#0A0A0C] border-white/10 w-28 h-8" />
                    <label className="flex items-center gap-1 text-xs text-red-400"><input type="checkbox" checked={!!it.unavailable} data-testid={`item-unavail-${e.id}-${idx}`} onChange={(ev) => setItem(e.id, idx, "unavailable", ev.target.checked)} className="h-3.5 w-3.5 accent-red-500" /> n/d</label>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => saveItems(e)} data-testid={`save-items-${e.id}`}>Salva prezzi</Button>
                <label className="inline-flex items-center gap-1 text-sm bg-white/5 hover:bg-white/10 rounded-md px-3 py-1.5 cursor-pointer" data-testid={`receipt-${e.id}`}><Upload className="h-4 w-4" /> Scontrino<input type="file" accept="image/*" className="hidden" onChange={(ev) => ev.target.files[0] && uploadReceipt(e.id, ev.target.files[0])} /></label>
                <Select onValueChange={(v) => setStatus(e.id, v)}>
                  <SelectTrigger data-testid={`errand-status-${e.id}`} className="w-44 bg-[#0A0A0C] border-white/10 h-9"><SelectValue placeholder="Cambia stato" /></SelectTrigger>
                  <SelectContent>{["acquistato", "in_consegna", "consegnato"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
          {mine.filter((e) => e.status !== "consegnato" && e.status !== "annullato").length === 0 && <div className="text-zinc-500 py-6 text-center">Nessuna commissione attiva.</div>}
        </div>
      </div>
    </div>
  );
}

function NavigateButton({ order }) {
  const pickupPhase = order.status === "rider_assegnato";
  const dLat = pickupPhase ? order.pickup_lat : order.dest_lat;
  const dLng = pickupPhase ? order.pickup_lng : order.dest_lng;
  const destLabel = pickupPhase ? `→ Ritiro: ${order.business_name}` : `→ Consegna: ${order.customer_name}`;
  const missing = dLat == null || dLng == null;
  const open = (provider) => {
    if (dLat == null || dLng == null) { toast.error("Coordinate GPS destinazione non disponibili"); return; }
    const build = (oLat, oLng) => {
      const origin = oLat != null ? `${oLat},${oLng}` : "";
      let url;
      if (provider === "waze") url = `https://waze.com/ul?ll=${dLat},${dLng}&navigate=yes`;
      else if (provider === "apple") url = `https://maps.apple.com/?daddr=${dLat},${dLng}${origin ? `&saddr=${origin}` : ""}&dirflg=d`;
      else url = `https://www.google.com/maps/dir/?api=1&destination=${dLat},${dLng}${origin ? `&origin=${origin}` : ""}&travelmode=driving`;
      window.open ? openExternalUrl(url) : (window.location.href = url);
    };
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((p) => build(p.coords.latitude, p.coords.longitude), () => build(null, null), { enableHighAccuracy: true, timeout: 4000 });
    } else build(null, null);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" disabled={missing} data-testid={`nav-map-${order.id}`} className="bg-[#00E676] text-black hover:bg-[#00c766] disabled:opacity-40"><Navigation className="h-4 w-4 mr-1" /> Naviga</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#1C1C1F] border-white/10 text-white">
        <div className="px-2 py-1.5 text-xs text-zinc-400">{destLabel}</div>
        <DropdownMenuItem data-testid={`nav-google-${order.id}`} onClick={() => open("google")}>Google Maps</DropdownMenuItem>
        <DropdownMenuItem data-testid={`nav-waze-${order.id}`} onClick={() => open("waze")}>Waze</DropdownMenuItem>
        <DropdownMenuItem data-testid={`nav-apple-${order.id}`} onClick={() => open("apple")}>Apple Maps</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NAV = [
  { key: "deliveries", label: "Consegne", path: "/rider", icon: Package },
  { key: "available", label: "Disponibili", path: "/rider/available", icon: Bike },
  { key: "market", label: "Mercato", path: "/rider/market", icon: ShoppingBasket },
  { key: "missions", label: "Missioni", path: "/rider/missions", icon: Target },
  { key: "livelli", label: "Livelli & Rewards", path: "/app/livelli", icon: Award },
  { key: "earnings", label: "Guadagni", path: "/rider/earnings", icon: Euro },
  { key: "wallet", label: "Portafoglio", path: "/rider/wallet", icon: Wallet },
  { key: "leaderboard", label: "Classifica", path: "/rider/leaderboard", icon: Trophy },
  { key: "map", label: "Mappa", path: "/rider/map", icon: MapIcon },
  { key: "ai", label: "Assistente AI", path: "/rider/ai", icon: Bot },
];

const RIDER_STATUSES = ["ritirato", "in_consegna", "consegnato"];

function AvailabilityToggle() {
  const { user, setUser } = useAuth();
  const [avail, setAvail] = useState(user?.available || false);
  const toggle = async (v) => { setAvail(v); const { data } = await api.put("/rider/availability", { available: v }); setUser(data); toast.success(v ? "Sei disponibile" : "Non disponibile"); };

  // Invio posizione GPS live REALE (watchPosition). Nessuna simulazione.
  useEffect(() => {
    if (!avail) return;
    if (!("geolocation" in navigator)) { toast.error("GPS non disponibile su questo dispositivo"); return; }
    let notified = false;
    const send = (lat, lng) => { api.put("/rider/location", { lat, lng }).catch(() => {}); };
    const watchId = navigator.geolocation.watchPosition(
      (p) => send(p.coords.latitude, p.coords.longitude),
      () => { if (!notified) { notified = true; toast.error("Consenti l'accesso alla posizione per il tracking live"); } },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [avail]);

  const label = { true: "Disponibile", false: "Offline" };
  return (
    <div className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-5 mb-6">
      <div><div className="font-semibold">Stato disponibilità</div><div className="text-sm text-zinc-400">{avail ? "Posizione live attiva · ricevi consegne" : "Offline"}</div></div>
      <div className="flex items-center gap-3"><span data-testid="rider-status-label" className={avail ? "text-[#00E676]" : "text-zinc-500"}>{label[avail]}</span><Switch data-testid="availability-switch" checked={avail} onCheckedChange={toggle} /></div>
    </div>
  );
}

function Deliveries() {
  const [orders, setOrders] = useState([]);
  const load = async () => { const { data } = await api.get("/orders"); setOrders(data.filter((o) => o.status !== "annullato")); };
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, []);
  const setStatus = async (id, status) => { try { await api.put(`/orders/${id}/status`, { status }); load(); toast.success("Stato aggiornato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const respond = async (id, action) => { try { await api.put(`/rider/orders/${id}/respond`, { action }); load(); toast.success(action === "accept" ? "Consegna accettata" : "Consegna rifiutata"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const confirmCash = async (id) => { try { await api.put(`/orders/${id}/confirm-cash`); load(); toast.success("Incasso contante confermato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div>
      <AvailabilityToggle />
      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} data-testid={`delivery-${o.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3"><span className="font-semibold">#{o.id.slice(-5)} · €{o.total.toFixed(2)}</span><StatusBadge status={o.status} /></div>
            <div className="text-sm space-y-1 mb-3">
              <div className="flex items-center gap-2 text-amber-400"><MapPin className="h-4 w-4" /> Ritiro: {o.business_name}, {o.business_address}</div>
              <div className="flex items-center gap-2 text-cyan-400"><MapPin className="h-4 w-4" /> Consegna: {o.customer_name}, {o.address}</div>
            </div>
            {(o.pickup_lat != null || o.dest_lat != null) && (
              <LiveMap className="mb-3" height={220} points={[
                (o.pickup_lat != null) && { lat: o.pickup_lat, lng: o.pickup_lng, label: "Ritiro", type: "business" },
                (o.dest_lat != null) && { lat: o.dest_lat, lng: o.dest_lng, label: "Consegna", type: "customer" },
              ].filter(Boolean)} />
            )}
            {o.status === "rider_assegnato" && !o.rider_accepted ? (
              <div className="rounded-lg bg-[#00E676]/10 border border-[#00E676]/40 p-3" data-testid={`assign-actions-${o.id}`}>
                <div className="text-sm font-medium mb-2">Nuova consegna assegnata — accetti?</div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => respond(o.id, "accept")} data-testid={`accept-order-${o.id}`} className="bg-[#00E676] text-black rounded-md"><CheckCircle2 className="h-4 w-4 mr-1" /> Accetta</Button>
                  <Button size="sm" variant="destructive" onClick={() => respond(o.id, "reject")} data-testid={`reject-order-${o.id}`}><XCircle className="h-4 w-4 mr-1" /> Rifiuta</Button>
                </div>
              </div>
            ) : (
            <div className="flex flex-wrap gap-2">
              <NavigateButton order={o} />
              <Button size="sm" variant="secondary" data-testid={`call-cust-${o.id}`} onClick={() => o.customer_phone ? (window.location.href = `tel:${o.customer_phone}`) : toast.info("Numero cliente non disponibile")}><Phone className="h-4 w-4 mr-1" /> Cliente</Button>
              <Button size="sm" variant="secondary" data-testid={`call-biz-${o.id}`} onClick={() => o.business_phone ? (window.location.href = `tel:${o.business_phone}`) : toast.info("Numero attività non disponibile")}><Phone className="h-4 w-4 mr-1" /> Locale</Button>
              <Select onValueChange={(v) => setStatus(o.id, v)}>
                <SelectTrigger data-testid={`delivery-status-${o.id}`} className="w-44 bg-[#0A0A0C] border-white/10"><SelectValue placeholder="Cambia stato" /></SelectTrigger>
                <SelectContent>{RIDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            )}
            {o.payment_method === "contanti" && (
              <div data-testid={`cash-block-${o.id}`} className="mt-3 rounded-lg bg-[#00E676]/5 border border-[#00E676]/20 p-3 text-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span>Contanti da incassare: <b className="text-[#00E676]">€{o.total.toFixed(2)}</b>{o.cash_amount_given != null ? ` · paga con €${o.cash_amount_given.toFixed(2)}` : ""}{o.cash_change != null ? ` · resto €${o.cash_change.toFixed(2)}` : ""}</span>
                  {o.cash_confirmed ? (
                    <span className="text-[#00E676] text-xs flex items-center gap-1" data-testid={`cash-confirmed-${o.id}`}><CheckCircle2 className="h-4 w-4" /> Incassato</span>
                  ) : ["ritirato", "in_consegna", "consegnato"].includes(o.status) ? (
                    <Button size="sm" data-testid={`confirm-cash-${o.id}`} onClick={() => confirmCash(o.id)} className="bg-[#00E676] text-black rounded-md"><Banknote className="h-4 w-4 mr-1" /> Conferma incasso</Button>
                  ) : null}
                </div>
                {o.cash_change > 50 && !o.cash_confirmed && <div className="text-amber-400 text-xs mt-1">Resto elevato: assicurati di avere il resto adeguato.</div>}
              </div>
            )}
          </div>
        ))}
        {orders.length === 0 && <div className="text-zinc-500 py-16 text-center">Nessuna consegna assegnata.</div>}
      </div>
    </div>
  );
}

function Available() {
  const [orders, setOrders] = useState([]);
  const prevCount = useRef(null);
  const load = async () => {
    const { data } = await api.get("/orders/available-deliveries");
    if (prevCount.current !== null && data.length > prevCount.current) play("rider_assigned");
    prevCount.current = data.length;
    setOrders(data);
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const accept = async (id) => { try { await api.put(`/orders/${id}/accept-delivery`); load(); toast.success("Consegna accettata"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} data-testid={`available-${o.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5 flex items-center justify-between">
          <div><div className="font-semibold">#{o.id.slice(-5)} · {o.business_name}</div><div className="text-sm text-zinc-400">{o.zone} · Guadagno €{o.delivery_fee.toFixed(2)}</div></div>
          <Button size="sm" data-testid={`accept-delivery-${o.id}`} onClick={() => accept(o.id)} className="bg-[#00E676] text-black rounded-md">Accetta</Button>
        </div>
      ))}
      {orders.length === 0 && <div className="text-zinc-500 py-16 text-center">Nessuna consegna disponibile.</div>}
    </div>
  );
}

function Earnings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/rider/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard testid="earn-today" label="Guadagno oggi" value={`€${s.earnings_today}`} accent icon={Euro} />
      <StatCard testid="earn-week" label="Guadagno settimana" value={`€${s.earnings_week}`} icon={Euro} />
      <StatCard testid="earn-month" label="Guadagno mese" value={`€${s.earnings_month}`} icon={Euro} />
      <StatCard testid="deliveries-total" label="Consegne totali" value={s.deliveries_total} icon={Package} />
      <div className="col-span-2 lg:col-span-4 bg-[#1C1C1F] border border-[#00E676]/30 rounded-lg p-6">
        <div className="flex items-center gap-3"><Trophy className="h-6 w-6 text-[#00E676]" /><div><div className="font-semibold">Obiettivo settimanale</div><div className="text-sm text-zinc-400">{s.count_week}/20 consegne — bonus €15 al raggiungimento</div></div></div>
        <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#00E676]" style={{ width: `${Math.min(100, s.count_week / 20 * 100)}%` }} /></div>
      </div>
    </div>
  );
}

function Leaderboard() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/rider/leaderboard").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} data-testid={`leaderboard-${i}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-4"><span className={`font-display font-black text-xl w-8 ${i < 3 ? "text-[#00E676]" : "text-zinc-600"}`}>{i + 1}</span><span className="font-medium">{r.name}</span></div>
          <span className="text-sm text-zinc-400">{r.deliveries} consegne</span>
        </div>
      ))}
    </div>
  );
}

const RTIER = { bronze: "#CD7F32", silver: "#C7CAD1", gold: "#FFD700", platinum: "#7DF9FF" };
const RMICONS = { package: Package, trophy: Trophy, map: MapPin };
const RLVICON = { bronze: Medal, silver: Award, gold: Trophy, platinum: Gem };

function RiderMissions() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/rider/missions").then(({ data }) => setData(data)).catch(() => {});
    api.get("/rider/rewards/unseen").then(({ data }) => {
      if (data && data.length) {
        confetti({ particleCount: 130, spread: 80, origin: { y: 0.35 }, colors: ["#00E676", "#FFD700", "#7DF9FF"] });
        data.slice(0, 3).forEach((r) => { const cash = r.reward?.cash_bonus ? ` +€${Number(r.reward.cash_bonus).toFixed(2)}` : ""; toast.success(`Missione «${r.mission_title}» — ${r.tier_label}!${cash} bonus`, { duration: 7000 }); });
        api.post("/rider/rewards/seen").catch(() => {});
      }
    }).catch(() => {});
  }, []);
  if (!data) return <div className="text-zinc-500 py-16 text-center">Caricamento…</div>;
  const lv = data.level;
  const LvIcon = RLVICON[lv.level] || Trophy;
  const c = RTIER[lv.level] || "#00E676";
  const span = lv.next_level_xp != null ? lv.next_level_xp - lv.current_level_xp : 1;
  const xpPct = lv.next_level_xp != null ? Math.min(100, Math.round(((lv.xp - lv.current_level_xp) / span) * 100)) : 100;
  return (
    <div className="space-y-6" data-testid="rider-missions">
      <div data-testid="rider-level-card" className="relative overflow-hidden rounded-2xl p-6 border border-white/10" style={{ background: `radial-gradient(120% 140% at 0% 0%, ${c}22, #131316 60%)` }}>
        <div className="flex items-center gap-5 flex-wrap">
          <div className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ background: `${c}22`, border: `1px solid ${c}66`, boxShadow: `0 0 30px ${c}33` }}><LvIcon className="h-8 w-8" style={{ color: c }} /></div>
          <div className="flex-1 min-w-[180px]">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Livello rider</div>
            <div className="text-2xl font-display font-bold" style={{ color: c }} data-testid="rider-level-label">{lv.level_label}</div>
            {lv.next_level ? (<div className="mt-2"><div className="h-2 rounded-full bg-white/10 overflow-hidden max-w-sm"><div className="h-full rounded-full" style={{ width: `${xpPct}%`, background: `linear-gradient(90deg, ${c}, #00E676)` }} /></div><div className="text-xs text-zinc-500 mt-1">{lv.xp} XP · prossimo {lv.next_level} a {lv.next_level_xp} XP</div></div>) : <div className="text-xs text-zinc-500 mt-1">{lv.xp} XP · livello massimo</div>}
          </div>
          <div className="text-center px-4 py-2 rounded-xl bg-white/5 border border-white/10"><div className="text-[#00E676] font-bold text-xl" data-testid="rider-bonus">€{Number(lv.bonus_earnings).toFixed(2)}</div><div className="text-[10px] uppercase tracking-wide text-zinc-500">Bonus</div></div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.missions.map((m) => {
          const Icon = RMICONS[m.icon] || Target;
          const pct = Math.min(100, Math.round((m.current_value / m.max_threshold) * 100));
          const col = m.tier_reached ? RTIER[m.tier_reached] : "#00E676";
          return (
            <div key={m.code} data-testid={`rider-mission-${m.code}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
              <div className="flex items-start gap-3 mb-3"><div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${col}22`, border: `1px solid ${col}55` }}><Icon className="h-5 w-5" style={{ color: col }} /></div><div className="flex-1"><div className="flex items-center gap-2"><h4 className="font-semibold">{m.title}</h4>{m.completed && <CheckCircle2 className="h-4 w-4 text-[#00E676]" />}</div><p className="text-xs text-zinc-500 mt-0.5">{m.description}</p></div></div>
              <div className="mb-2 flex items-center justify-between text-xs"><span className="text-zinc-400">Progresso: <b className="text-white">{m.current_value}</b> / {m.max_threshold}</span>{m.tier_reached && <span className="uppercase font-semibold" style={{ color: col }}>{m.tier_reached}</span>}</div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${col}, #00E676)` }} /></div>
              <div className="mt-3 flex flex-wrap gap-1.5">{m.tiers.map((t) => { const done = m.current_value >= t.threshold; const tc = RTIER[t.key]; return (<div key={t.key} className="text-[10px] px-2 py-1 rounded-md flex items-center gap-1" style={{ background: done ? `${tc}22` : "rgba(255,255,255,0.03)", border: `1px solid ${done ? tc + "66" : "rgba(255,255,255,0.08)"}`, color: done ? tc : "#a1a1aa" }}>{done ? <CheckCircle2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}<span>{t.label} · {t.threshold}</span>{t.reward.cash_bonus ? <span className="text-[#00E676]">+€{t.reward.cash_bonus}</span> : null}</div>); })}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RiderDashboard() {
  return (
    <DashboardLayout title="Dashboard Rider" nav={NAV}>
      <Routes>
        <Route index element={<Deliveries />} />
        <Route path="available" element={<Available />} />
        <Route path="market" element={<RiderMarket />} />
        <Route path="missions" element={<RiderMissions />} />
        <Route path="earnings" element={<Earnings />} />
        <Route path="wallet" element={<WalletStatement />} />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="map" element={<div className="space-y-3"><h3 className="font-semibold flex items-center gap-2"><MapIcon className="h-5 w-5 text-[#00E676]" /> Mappa viva del territorio</h3><TerritoryMap height={520} /></div>} />
        <Route path="ai" element={<RiderAI />} />
      </Routes>
    </DashboardLayout>
  );
}
